import type {
  Lfa_Account_Services_V2__Input,
  Lfa_Account_Service_V2__Input,
  Lfa_Calendar_Credentials__Input,
  Lfa_Calendar_V2__Input,
  Lfa_Generated_Credential__Input,
  Lfa_Office_Change_V2__Input,
  Lfa_Office_Changeset_Entry_V2__Input,
  Lfa_Service_Calendar_Ref__Input,
  LfaCalendarV2FieldsFragment,
} from '~/graphql/types';
import type { OfficeChangeFragment, ServicesV2Config } from './types';

const serializeCalendarCredentials = (
  calendar: LfaCalendarV2FieldsFragment,
): Lfa_Calendar_Credentials__Input | null => {
  const { credentials } = calendar;
  if (!credentials) return null;

  switch (credentials.__typename) {
    case 'LFA_Calendar_Credentials_Inline':
      return {
        Inline: {
          username: credentials.username,
          password: credentials.password,
        },
      };
    case 'LFA_Calendar_Credentials_Ref':
      return {
        Ref: { id: credentials.id },
      };
    default:
      return null;
  }
};

const serializeCalendarRef = (ref: {
  calendarId: string;
  weight: number;
  note?: string | null;
}): Lfa_Service_Calendar_Ref__Input => ({
  calendarId: ref.calendarId,
  weight: ref.weight,
  note: ref.note,
});

const serializeOfficeChange = (
  change: OfficeChangeFragment,
): Lfa_Office_Change_V2__Input => {
  switch (change.__typename) {
    case 'LFA_Office_Change_Add_V2':
      return {
        Add: {
          serviceId: change.serviceId,
          locations: change.addLocations.map(l => ({
            type: l.type,
            note: l.note,
            weight: l.weight,
          })),
          criteria: change.addCriteria.map(c => ({
            id: c.id,
            text: c.text,
          })),
          calendars: change.addCalendars.map(serializeCalendarRef),
        },
      };
    case 'LFA_Office_Change_Remove_V2':
      return {
        Remove: { serviceId: change.serviceId },
      };
    case 'LFA_Office_Change_Mutate_V2':
      return {
        Mutate: {
          serviceId: change.serviceId,
          ...(change.mutateLocations && {
            locations: change.mutateLocations.map(l => ({
              type: l.type,
              note: l.note,
              weight: l.weight,
            })),
          }),
          ...(change.mutateAddCriteria && {
            addCriteria: change.mutateAddCriteria.map(c => ({
              id: c.id,
              text: c.text,
            })),
          }),
          ...(change.removeCriteria && {
            removeCriteria: change.removeCriteria,
          }),
          ...(change.mutateAddCalendars && {
            addCalendars: change.mutateAddCalendars.map(serializeCalendarRef),
          }),
          ...(change.removeCalendarIds && {
            removeCalendarIds: change.removeCalendarIds,
          }),
        },
      };
  }
};

const serializeServicesV2Input = ({
  credentials,
  calendars,
  accountServices,
  officeChangesets,
}: ServicesV2Config): Lfa_Account_Services_V2__Input => {
  const credentialInputs: Array<Lfa_Generated_Credential__Input> =
    credentials.map(c => ({
      id: c.id,
      name: c.name,
      username: c.username,
      password: c.password,
      note: c.note,
    }));

  const calendarInputs: Array<Lfa_Calendar_V2__Input> = calendars.map(cal => {
    const creds = serializeCalendarCredentials(cal);
    return {
      id: cal.id,
      name: cal.name,
      type: cal.type,
      note: cal.note,
      link: cal.link,
      officeIds: cal.officeIds,
      ...(creds && { credentials: creds }),
    };
  });

  const accountServiceInputs: Array<Lfa_Account_Service_V2__Input> =
    accountServices.map(s => ({
      id: s.id,
      locations: s.locations.map(l => ({
        type: l.type,
        note: l.note,
        weight: l.weight,
      })),
      criteria: s.criteria.map(c => ({ id: c.id, text: c.text })),
      calendars: s.calendars.map(serializeCalendarRef),
    }));

  const changesetInputs: Array<Lfa_Office_Changeset_Entry_V2__Input> =
    officeChangesets.map(entry => ({
      officeId: entry.officeId,
      changeset: {
        service: entry.changeset.service.map(serializeOfficeChange),
      },
    }));

  return {
    credentials: credentialInputs,
    calendars: calendarInputs,
    accountServices: accountServiceInputs,
    officeChangesets: changesetInputs,
  };
};

export default serializeServicesV2Input;
